// src/components/NuestrosClientesView.jsx
// Vista pública de "Nuestros Clientes": álbumes de fotos + reseñas
// aprobadas. La usan tanto la pantalla dentro de la PWA (con botón para
// dejar reseña) como la pública sin login (solo lectura).
import React, { useEffect, useState, useRef } from 'react';
import { listarAlbumsPublicos, listarResenasPublicas, resumenResenasPublico, CATEGORIAS_RESENA, logVisitaClientes, setClientesTrackingOptOut } from '../services/nuestrosClientesService';

function Estrellas({ n, size = 14 }) {
  return (
    <span style={{ color: 'var(--gold, #FFB800)', fontSize: size, letterSpacing: 1 }}>
      {'★'.repeat(n)}{'☆'.repeat(5 - n)}
    </span>
  );
}

// El visor es SIEMPRE oscuro, en los dos temas de la app -- por eso no usa
// las variables de tema para el texto: en modo claro var(--white) vale
// #1A2733 (azul casi negro) y el título quedaba invisible sobre el fondo
// negro. Acá los colores van fijos "sobre oscuro".
const SOBRE_OSCURO = {
  texto: '#FFFFFF',
  textoSuave: '#C9D4E4',
  marca: '#FF8C33',
  superficie: 'rgba(22,32,64,0.9)',
  borde: 'rgba(255,255,255,0.16)',
};

// idx = -1 es la portada del álbum (título grande de marca); 0..N-1 son
// las fotos en el orden que armó el admin, cada una con su leyenda debajo.
function AlbumModal({ album, onClose }) {
  const [idx, setIdx] = useState(-1);
  const touchX = useRef(null);
  if (!album) return null;
  const total = album.fotos.length;
  const foto = idx >= 0 ? album.fotos[idx] : null;

  const ir = (delta) => setIdx((i) => Math.max(-1, Math.min(total - 1, i + delta)));

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(delta) > 40) ir(delta < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)', zIndex: 400, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14 }}>
        <p style={{ color: SOBRE_OSCURO.textoSuave, fontSize: 12, fontWeight: 700 }}>
          {idx === -1 ? 'Portada' : `${idx + 1}/${total}`}
        </p>
        <button onClick={onClose} aria-label="Cerrar álbum" style={{
          background: SOBRE_OSCURO.superficie, border: `1px solid ${SOBRE_OSCURO.borde}`,
          color: SOBRE_OSCURO.texto, width: 44, height: 44, borderRadius: 12, fontSize: 16,
        }}>✕</button>
      </div>

      <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {idx === -1 ? (
          <div style={{
            flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
            padding: '32px 24px', textAlign: 'center', overflow: 'hidden',
          }}>
            {album.portada && (
              <>
                <img src={album.portada.url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                {/* Degradado: la foto se ve nítida arriba y se oscurece hacia
                    abajo, donde va el texto -- así se lee siempre, sin tapar
                    la foto con un recuadro. */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to bottom, rgba(10,22,40,0.15) 0%, rgba(10,22,40,0.55) 55%, rgba(10,22,40,0.94) 100%)',
                }} />
              </>
            )}
            <div style={{ position: 'relative' }}>
              <h2 style={{
                fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, letterSpacing: 0.5,
                color: SOBRE_OSCURO.texto, lineHeight: 1.1, marginBottom: 10,
                textShadow: '0 2px 12px rgba(0,0,0,0.6)',
              }}>
                {album.tituloPublico || 'Un viaje con Click Travel Agency'}
              </h2>
              <div style={{ width: 44, height: 3, background: 'var(--orange)', borderRadius: 2, margin: '0 auto 10px' }} />
              <p style={{ fontSize: 10.5, color: SOBRE_OSCURO.marca, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.2 }}>
                Click Travel Agency
              </p>
              <p style={{ marginTop: 16, fontSize: 11.5, color: SOBRE_OSCURO.textoSuave }}>
                {total} foto{total !== 1 ? 's' : ''} · desliza para ver la historia →
              </p>
            </div>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px', overflow: 'hidden', minHeight: 0 }}>
              {foto.tipo === 'video' ? (
                <video src={foto.url} controls playsInline style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 10 }} />
              ) : (
                <img src={foto.url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 10, objectFit: 'contain' }} />
              )}
            </div>
            {foto.caption && (
              <p style={{
                textAlign: 'center', color: SOBRE_OSCURO.texto, fontSize: 13.5, fontWeight: 600,
                padding: '20px 20px 4px', lineHeight: 1.4,
                background: 'linear-gradient(to bottom, rgba(10,22,40,0) 0%, rgba(10,22,40,0.92) 60%)',
              }}>
                {foto.caption}
              </p>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16 }}>
        <button disabled={idx === -1} onClick={() => ir(-1)} style={{
          background: SOBRE_OSCURO.superficie, border: `1px solid ${SOBRE_OSCURO.borde}`, color: SOBRE_OSCURO.texto,
          borderRadius: 12, padding: '12px 20px', fontSize: 13.5, fontWeight: 600, minHeight: 44, opacity: idx === -1 ? 0.4 : 1,
        }}>‹ Anterior</button>
        <button disabled={idx === total - 1} onClick={() => ir(1)} style={{
          background: SOBRE_OSCURO.superficie, border: `1px solid ${SOBRE_OSCURO.borde}`, color: SOBRE_OSCURO.texto,
          borderRadius: 12, padding: '12px 20px', fontSize: 13.5, fontWeight: 600, minHeight: 44, opacity: idx === total - 1 ? 0.4 : 1,
        }}>Siguiente ›</button>
      </div>
    </div>
  );
}

const ALBUMS_VISIBLES_INICIAL = 6;
const RESENAS_VISIBLES_INICIAL = 3;

// Visor simple a pantalla completa para una foto suelta (las que adjunta
// un cliente a su reseña) -- no tiene navegación entre fotos como el
// AlbumModal, solo ampliar y cerrar.
function FotoAmpliada({ url, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)', zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <button onClick={onClose} aria-label="Cerrar foto" style={{
        position: 'absolute', top: 14, right: 14, background: SOBRE_OSCURO.superficie, border: `1px solid ${SOBRE_OSCURO.borde}`,
        color: SOBRE_OSCURO.texto, width: 44, height: 44, borderRadius: 12, fontSize: 16,
      }}>✕</button>
      <img src={url} alt="" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 10, objectFit: 'contain' }} />
    </div>
  );
}

export default function NuestrosClientesView({ canReview, onLeaveReview, highlightShareToken }) {
  const [albums, setAlbums] = useState([]);
  const [resenas, setResenas] = useState([]);
  const [resumen, setResumen] = useState({ promedio: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [openAlbum, setOpenAlbum] = useState(null);
  const [verTodosAlbums, setVerTodosAlbums] = useState(false);
  const [verTodasResenas, setVerTodasResenas] = useState(false);
  const [fotoAmpliada, setFotoAmpliada] = useState(null);

  useEffect(() => {
    Promise.all([listarAlbumsPublicos(), listarResenasPublicas(), resumenResenasPublico()])
      .then(([a, r, s]) => { setAlbums(a); setResenas(r); setResumen(s); })
      .finally(() => setLoading(false));
    // ?notrack=1 en el link -- para que Yoedis pueda abrirlo una vez en cada
    // teléfono/navegador propio (distinto al que usa para el panel admin) y
    // que esas visitas de prueba nunca se cuenten ahí en adelante. Mismo
    // patrón que Vuelos Guyana.
    if (new URLSearchParams(window.location.search).get('notrack') === '1') {
      setClientesTrackingOptOut(true);
    }
    // canReview solo es true en la pantalla dentro de la app (con login) --
    // la pública sin login siempre entra como 'publico'.
    logVisitaClientes(canReview ? 'autenticado' : 'publico');
  }, []);

  useEffect(() => {
    if (!highlightShareToken || !albums.length) return;
    const el = document.getElementById(`album-${highlightShareToken}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightShareToken, albums]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>;
  }

  return (
    <div style={{ padding: '16px 16px 40px' }}>
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)', marginBottom: 6 }}>Nuestros Clientes</h2>
        {resumen.total > 0 ? (
          <button
            onClick={() => document.getElementById('nc-opiniones')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            style={{ background: 'transparent', padding: 0, color: 'var(--muted)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Estrellas n={Math.round(resumen.promedio)} size={15} /> {resumen.promedio} · {resumen.total} reseña{resumen.total !== 1 ? 's' : ''}
          </button>
        ) : (
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>Fotos reales de clientes que ya viajaron con nosotros</p>
        )}
        {canReview && (
          <div style={{ marginTop: 14 }}>
            <button onClick={onLeaveReview} style={{ background: 'var(--orange)', color: 'white', fontWeight: 700, fontSize: 13, borderRadius: 20, padding: '10px 20px' }}>
              ⭐ Dejar mi reseña
            </button>
            <p style={{ marginTop: 8, color: 'var(--muted)', fontSize: 11.5 }}>¿Viajaste con nosotros? Cuéntanos cómo te fue</p>
          </div>
        )}
      </div>

      {albums.length > 0 && (
        <>
          <p style={{ fontSize: 18, color: 'var(--white)', fontWeight: 800, marginBottom: 12 }}>Así viajan con nosotros</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14, marginBottom: 14 }}>
            {(verTodosAlbums ? albums : albums.slice(0, ALBUMS_VISIBLES_INICIAL)).map((a) => (
              <button key={a.id} id={`album-${a.shareToken}`} onClick={() => setOpenAlbum(a)} style={{
                textAlign: 'left', background: 'transparent', padding: 0, width: '100%', color: 'inherit',
              }}>
                <div style={{
                  position: 'relative', borderRadius: 14, overflow: 'hidden', aspectRatio: '1',
                  border: a.shareToken === highlightShareToken ? '2px solid var(--orange)' : '1px solid var(--navy-border)',
                }}>
                  {a.portada ? (
                    <img src={a.portada.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    // Álbum solo con video(s): no hay foto para usar de miniatura.
                    <video src={a.fotos[0]?.url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  {a.fotos.length > 1 && (
                    <span style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(10,22,40,0.75)', color: 'white', fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '2px 7px' }}>+{a.fotos.length - 1}</span>
                  )}
                </div>
                {a.tituloPublico && (
                  <p style={{ marginTop: 6, fontSize: 11.5, fontWeight: 700, color: 'var(--white)', lineHeight: 1.3 }}>{a.tituloPublico}</p>
                )}
              </button>
            ))}
          </div>
          {albums.length > ALBUMS_VISIBLES_INICIAL && (
            <button onClick={() => setVerTodosAlbums((v) => !v)} style={{
              width: '100%', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)',
              fontWeight: 700, fontSize: 12.5, borderRadius: 12, padding: '11px 0', marginBottom: 26,
            }}>
              {verTodosAlbums ? 'Ver menos ↑' : `Ver los ${albums.length} álbumes (${albums.length - ALBUMS_VISIBLES_INICIAL} más) ↓`}
            </button>
          )}
          {albums.length <= ALBUMS_VISIBLES_INICIAL && <div style={{ marginBottom: 12 }} />}
        </>
      )}

      {resenas.length > 0 && (
        <>
          <p id="nc-opiniones" style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>OPINIONES</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(verTodasResenas ? resenas : resenas.slice(0, RESENAS_VISIBLES_INICIAL)).map((r) => (
              <div key={r.id} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>{r.nombre}</span>
                  <Estrellas n={r.rating} />
                </div>
                <p style={{ fontSize: 10.5, color: 'var(--orange)', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
                  {CATEGORIAS_RESENA.find((c) => c.id === r.categoria)?.label || r.categoria}
                </p>
                {r.comentario?.trim() && (
                  <p style={{ fontSize: 13, color: 'var(--off-white, var(--white))', lineHeight: 1.5 }}>{r.comentario}</p>
                )}
                {r.fotosUrls?.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {r.fotosUrls.map((url, i) => (
                      <button key={i} onClick={() => setFotoAmpliada(url)} style={{ background: 'transparent', padding: 0 }}>
                        <img src={url} alt="" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', display: 'block' }} />
                      </button>
                    ))}
                  </div>
                )}
                {r.respuesta_publica && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--navy-border)' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 3 }}>Respuesta de Click Travel Agency</p>
                    <p style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>{r.respuesta_publica}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          {resenas.length > RESENAS_VISIBLES_INICIAL && (
            <button onClick={() => setVerTodasResenas((v) => !v)} style={{
              width: '100%', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)',
              fontWeight: 700, fontSize: 12.5, borderRadius: 12, padding: '11px 0', marginTop: 12,
            }}>
              {verTodasResenas ? 'Ver menos ↑' : `Ver las ${resenas.length} reseñas (${resenas.length - RESENAS_VISIBLES_INICIAL} más) ↓`}
            </button>
          )}
        </>
      )}

      {albums.length === 0 && resenas.length === 0 && (
        <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, padding: '30px 0' }}>Todavía no hay contenido publicado.</p>
      )}

      {openAlbum && <AlbumModal album={openAlbum} onClose={() => setOpenAlbum(null)} />}
      {fotoAmpliada && <FotoAmpliada url={fotoAmpliada} onClose={() => setFotoAmpliada(null)} />}
    </div>
  );
}
